/**
 * Session hook backed by /api/auth/session.
 *
 * The session endpoint reads the `bo_session` cookie (set by either the
 * legacy bo-chat3 login OR the new OAuth bridge) and returns a small user
 * descriptor. We refresh-token-rotate automatically on first 401, then
 * retry once — silent renewal that's invisible to the UI.
 *
 * UX optimization: cache user info di localStorage (display-only — NOT
 * the JWT). Pas first mount, hydrate state dari cache sehingga avatar
 * muncul instan, sambil fetchSession() validate di background. Kalau
 * server bilang 401, kita evict cache. Token sebenernya tetap di HttpOnly
 * cookie — localStorage cuma display hint. Worst case kalau XSS: attacker
 * cuma tahu user email/role/secret_key, bukan JWT.
 */

import { useEffect, useSyncExternalStore } from 'react';

const LS_KEY = 'bo_session_user_hint';

export interface SessionUser {
  uid: string;
  email: string;
  role: string;
  secret_key: string;
}

export interface SessionState {
  loading: boolean;
  authenticated: boolean;
  user: SessionUser | null;
  error: string | null;
}

/**
 * Load cached user dari localStorage. Validate shape sebelum trust —
 * stored value bisa di-tampered. Kalau invalid, return null + clear key.
 */
function loadCachedUser(): SessionUser | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.uid === 'string' &&
      typeof parsed.email === 'string'
    ) {
      return parsed as SessionUser;
    }
    localStorage.removeItem(LS_KEY);
    return null;
  } catch {
    return null;
  }
}

function saveCachedUser(user: SessionUser | null) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (user) {
      localStorage.setItem(LS_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(LS_KEY);
    }
  } catch {
    /* quota exceeded / disabled — ignore */
  }
}

// Hydrate initial state dari localStorage hint. Authenticated=true secara
// optimistik supaya UserMenu langsung render avatar; fetchSession() di-
// trigger pas first useSession() mount dan akan flip ke false kalau ternyata
// cookie expired / invalid.
const cachedUser = loadCachedUser();
const initial: SessionState = {
  loading: cachedUser === null, // gak loading kalau punya hint (langsung pakai cache)
  authenticated: cachedUser !== null,
  user: cachedUser,
  error: null,
};

let state: SessionState = initial;
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => state;
const set = (next: Partial<SessionState>) => {
  state = { ...state, ...next };
  for (const l of listeners) l();
};

let inflight: Promise<void> | null = null;

async function fetchSession(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    // Kalau punya cache hint, jangan flip ke loading=true (avoid spinner
    // flicker). Cuma flip pas truly cold-start.
    if (!state.user) {
      set({ loading: true, error: null });
    } else {
      set({ error: null });
    }
    try {
      let res = await fetch('/api/auth/session', { credentials: 'include' });

      // If access token expired, try silent refresh once via OAuth bridge,
      // then retry the session check.
      if (res.status === 401) {
        const refreshOk = await silentRefresh();
        if (refreshOk) {
          res = await fetch('/api/auth/session', { credentials: 'include' });
        }
      }

      if (!res.ok) {
        saveCachedUser(null);
        set({ loading: false, authenticated: false, user: null, error: null });
        return;
      }
      const body = await res.json();
      if (body?.authenticated && body.user) {
        saveCachedUser(body.user);
        set({ loading: false, authenticated: true, user: body.user, error: null });
      } else {
        saveCachedUser(null);
        set({ loading: false, authenticated: false, user: null, error: null });
      }
    } catch (e) {
      // Network error — jangan langsung evict cache (offline → user still
      // wants avatar visible). Cuma flag error, keep cache.
      set({ loading: false, error: (e as Error).message });
    }
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/playground/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function useSession(): SessionState {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);

  // On first mount only, kick off the session fetch.
  useEffect(() => {
    if (state.loading || (!state.user && !state.error)) {
      void fetchSession();
    }
  }, []);

  return value;
}

/** Force a session re-fetch (e.g., after login completes). */
export function refreshSession(): Promise<void> {
  return fetchSession();
}

/** Compatibility helper: use after a known login event. */
export function markLoggedOut(): void {
  saveCachedUser(null);
  set({ loading: false, authenticated: false, user: null, error: null });
}
